import AuthGuard from '../components/AuthGuard';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import InnerLayout from '../components/InnerLayout';
import { api } from '../lib/api';

export default function WalletPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<any[]>([]);
  const validTypes = ['all', 'cash', 'bucang', 'huifu'];
  const initialType = searchParams.get('type');
  const [type, setTypeState] = useState<string>(
    initialType && validTypes.includes(initialType) ? initialType : 'all'
  );
  const setType = (t: string) => {
    setTypeState(t);
    setSearchParams(t === 'all' ? {} : { type: t }, { replace: true });
  };
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const sentinel = useRef<HTMLDivElement | null>(null);

  async function load(p?: number, reset?: boolean) {
    if (loading) return; setLoading(true);
    const t = type === 'all' ? undefined : type;
    const q: any = { per_page: 20 };
    if (t) q.type = t;
    if (p) q.page = p;
    try {
      const { data } = await api.get('/wallet/transactions', { params: q });
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setLogs(prev => reset ? arr : [...prev, ...arr]);
      const next = data?.next_page ?? null;
      setHasMore(!!next);
      setPage(next ?? (p ?? 1));
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLogs([]);
    setPage(1);
    setHasMore(true);
    load(1, true);
  }, [type]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && hasMore && !loading) {
        load(page || 1);
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [sentinel.current, hasMore, loading, page]);

  const list = useMemo(() => {
    const mapType = (t: string) => ({ cash: '现金钱包', bucang: '补偿资金', huifu: '恢复资产' }[t] ?? '');

    return logs.map((l) => {
      const time = l.created_at ? new Date(l.created_at) : new Date();
      const ts = `${time.getFullYear()}-${String(time.getMonth() + 1).padStart(2, "0")}-${String(time.getDate()).padStart(2, "0")} ${String(time.getHours()).padStart(2, "0")}:${String(time.getMinutes()).padStart(2, "0")}`;
      const sign = l.direction === 'decrement' ? '-' : '+';
      const amount = `${sign}${Number(l.amount ?? 0).toFixed(2)}`;
      const title = l.note ?? (l.task_id ? '任务奖励' : '账变');
      return { title, amount, type: mapType(l.wallet_type), wallet_type: l.wallet_type, direction: l.direction, time: ts };
    });
  }, [logs]);

  return (
    <div
      className="max-w-md mx-auto min-h-screen bg-[#8b0000]"
    >
      <AuthGuard />

      {/* Header */}
      <div className="relative pt-4 px-4">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black mb-2 relative z-10"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div className="flex flex-col items-center gap-3 justify-center relative mt-[-40px] text-white">
          <h4>资金明细</h4>
        </div>
      </div>

      <div className="mx-auto max-w-md px-4 mt-5">
        <div className="rounded-2xl bg-white p-2">
          <div className="flex items-center gap-4 border-b px-3 py-2 text-[12px] justify-around flex-wrap">
            <button className={type === 'all' ? 'text-[#0095ff]' : ''} onClick={() => setType('all')}>全部</button>
            <button className={type === 'cash' ? 'text-[#0095ff]' : ''} onClick={() => setType('cash')}>现金</button>
            <button className={type === 'bucang' ? 'text-[#0095ff]' : ''} onClick={() => setType('bucang')}>补偿金</button>
            <button className={type === 'huifu' ? 'text-[#0095ff]' : ''} onClick={() => setType('huifu')}>恢复资产</button> 
          </div>
          <div className="space-y-3 p-3">
            {list.map((i, idx) => (
              <div key={idx} className="rounded-xl bg-[#f9fafb] p-3 shadow border-[1px] border-[#e5e7eb]">
                <div className="flex justify-between">
                  <div>
                    <div className="text-sm">{i.title}</div>
                    <div className="text-[12px] text-gray-500">{i.time}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-600">{i.amount}</div>
                    <div className="text-[12px] text-gray-500">{i.type}</div>
                  </div>
                </div>
                {/* {i.wallet_type === 'bucang' && i.direction === 'decrement' && <div className="mt-2 text-[11px] text-orange-500">大额提现T+3工作日到账</div>} */}
              </div>
            ))}
            {list.length === 0 && (<div className="rounded-2xl bg-gray-50 p-6 text-center text-[12px] text-gray-500">暂无记录</div>)}
            <div ref={sentinel} />
          </div>
        </div>
      </div>
    </div>
  );
}
